'use client';

import React, { useRef, useState, useEffect } from 'react';
import Pagination from '../Pagination/Pagination';
import { ArticleCard } from './ArticleCard';
import { ArticlesPaginatedListProps } from './types';
import Tag from '../Tag/Tag';
import { LEARNING_HUB_BASE_PATH } from '@/lib/constants';

export const ArticlesPaginatedList: React.FC<ArticlesPaginatedListProps> = ({
  articles,
  categories = [],
  initialCategory = 'All Articles',
  initialPage = 1,
  itemsPerPage = 9,
  globalBrandLogo,
}) => {
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const statusRef = useRef<HTMLDivElement>(null);

  const getFilteredArticles = (category: string) => {
    if (category === 'All Articles') {
      return articles;
    }

    return articles.filter((article) => {
      const matchesCategory = article.category?.toLowerCase() === category.toLowerCase();
      const matchesCategories = article.categories?.some(
        (cat) => cat.toLowerCase() === category.toLowerCase(),
      );

      return matchesCategory || matchesCategories;
    });
  };

  // Sync state with back/forward history navigation.
  // This is OK because the state update happens inside an external browser event callback.
  useEffect(() => {
    const handlePopState = () => {
      const pathname = window.location.pathname;
      const params = new URLSearchParams(window.location.search);

      let category = 'All Articles';
      if (pathname.startsWith(LEARNING_HUB_BASE_PATH)) {
        const subPath = pathname.substring(LEARNING_HUB_BASE_PATH.length);
        const segments = subPath.split('/').filter(Boolean);
        if (segments.length > 0) {
          const rawSlug = segments[0];
          const decodedSlug = decodeURIComponent(rawSlug).toLowerCase();

          const matched = categories.find((cat) => {
            return cat.slug.toLowerCase() === decodedSlug || cat.name.toLowerCase() === decodedSlug;
          });
          if (matched) {
            category = matched.name;
          }
        }
      }

      const page = Number(params.get('page')) || 1;
      setActiveCategory(category);
      setCurrentPage(page);
    };
    
    handlePopState();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [categories]);

  // Filter articles based on activeCategory
  const filteredArticles = getFilteredArticles(activeCategory);

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);

  // Ensure current page is within valid range of filtered results.
  const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));

  const startIndex = (validCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentArticles = filteredArticles.slice(startIndex, endIndex);

  const getNewUrl = (categoryName: string, page: number) => {
    const basePath = LEARNING_HUB_BASE_PATH;

    if (categoryName === 'All Articles') {
      const params = new URLSearchParams();
      if (page > 1) {
        params.set('page', page.toString());
      }
      const queryString = params.toString();
      return queryString ? `${basePath}?${queryString}` : basePath;
    }

    const catInfo = categories.find((c) => c.name.toLowerCase() === categoryName.toLowerCase());
    const catSlug = catInfo ? catInfo.slug : encodeURIComponent(categoryName);

    const path = `${basePath}/${catSlug}`;

    const params = new URLSearchParams();
    if (page > 1) {
      params.set('page', page.toString());
    }

    const queryString = params.toString();
    return queryString ? `${path}?${queryString}` : path;
  };

  const scrollToExplore = () => {
    const exploreSection = document.querySelector('#explore-articles');
    if (exploreSection) {
      exploreSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setCurrentPage(1);

    // Update URL shallowly
    const newUrl = getNewUrl(category, 1);
    if (newUrl) {
      window.history.pushState(null, '', newUrl);
    }

    scrollToExplore();

    const nextFilteredArticles = getFilteredArticles(category);
    const nextTotalPages = Math.ceil(nextFilteredArticles.length / itemsPerPage) || 1;

    // Announce to AT
    if (statusRef.current) {
      statusRef.current.textContent = `Filtered by category ${category}. Showing page 1 of ${nextTotalPages}.`;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    // Update URL shallowly
    const newUrl = getNewUrl(activeCategory, page);
    if (newUrl) {
      window.history.pushState(null, '', newUrl);
    }

    scrollToExplore();

    // Announce the page change to AT
    if (statusRef.current) {
      const start = (page - 1) * itemsPerPage + 1;
      const end = Math.min(page * itemsPerPage, filteredArticles.length);
      statusRef.current.textContent = `Page ${page} of ${totalPages}. Showing articles ${start} to ${end}.`;
    }
  };

  return (
    <>
      <div
        ref={statusRef}
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className='sr-only'
      />

      {categories.length > 0 && (
        <div className='flex flex-wrap gap-2.5 mb-7'>
          {categories.map((cat) => {
            const isActive = cat.name.toLowerCase() === activeCategory.toLowerCase();
            const href = getNewUrl(cat.name, 1);
            return (
              <Tag
                key={cat.name}
                label={cat.name}
                isActive={isActive}
                href={href}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  handleCategoryChange(cat.name);
                }}
                ignoreSessionParams={true}
              />
            );
          })}
        </div>
      )}

      {/* Divider */}
      {categories.length > 0 && <div className='border-b-2 border-border-global mb-7' />}

      {currentArticles.length > 0 ? (
        <ul className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-5 list-none'>
          {currentArticles.map((article, index) => (
            <li
              key={article.sysId}
              className='flex justify-center'
            >
              <ArticleCard
                article={article}
                index={startIndex + index}
                className='w-full min-w-0 pb-2'
                globalBrandLogo={globalBrandLogo}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className='text-center py-12 text-primary-500 font-instrument text-lg'>
          No articles found in this category.
        </div>
      )}

      {totalPages > 1 && (
        <div className='mt-12 md:mt-32'>
          <Pagination
            currentPage={validCurrentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
};
